import {
	faArrowRight,
	faCircleCheck,
	faCircleHalfStroke,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Filter } from "lucide-react";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import {
	Button,
	Card,
	Col,
	Collapse,
	Container,
	Form,
	Row,
} from "react-bootstrap";
import { Ban } from "react-bootstrap-icons";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import "../css/ProgramList.css";
import authService from "../services/authService";
import NotificationService from "../services/NotificationService";
import NotificationOptIn from "./NotificationOptIn";
import ClientMilestones from "./ClientMilestones";

const ProgramListComponent = ({ clientId }) => {
	const [programs, setPrograms] = useState([]);
	const [statusData, setStatusData] = useState({});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedDate, setSelectedDate] = useState(
		new Date().toLocaleDateString("en-CA")
	);
	const [showFilter, setShowFilter] = useState(false);
	const navigate = useNavigate();

	const [showOptIn, setShowOptIn] = useState(false);
	const [user, setUser] = useState(null);
	const maxDate = `${new Date().getFullYear() + 1}-12-31`;

	useEffect(() => {
		const initUserAndOptIn = async () => {
			try {
				const [userResponse, subscription] = await Promise.all([
					api.get("/users/me/"),
					NotificationService.checkSubscription(),
				]);
				setUser(userResponse.data);

				setShowOptIn(
					authService.isClient() &&
						!subscription &&
						userResponse.data?.receives_notifications
				);
			} catch (error) {
				console.error("Error fetching user data:", error);
			}
		};

		initUserAndOptIn();
	}, []);

	const toDateString = (date) => {
		return date.toLocaleDateString("en-CA");
	};

	const parseDate = (dateString) => {
		const [year, month, day] = dateString.split("-").map(Number);
		return new Date(year, month - 1, day);
	};

	function getStartOfWeek(date) {
		const d = new Date(date);
		const day = d.getDay();
		const diff = d.getDate() - day + (day === 0 ? -6 : 1);
		return new Date(d.setDate(diff));
	}

	function formatDateRange(start, end) {
		const weekStart = parseDate(start);
		const weekEnd = parseDate(end);

		return `${weekStart.toLocaleDateString("en-AU", {
			day: "numeric",
			month: "short",
			year: "numeric",
		})} - ${weekEnd.toLocaleDateString("en-AU", {
			day: "numeric",
			month: "short",
			year: "numeric",
		})}`;
	}

	const getCurrentWeekStart = useCallback(() => {
		return toDateString(getStartOfWeek(parseDate(selectedDate)));
	}, [selectedDate]);

	const getCurrentWeekEnd = useCallback(() => {
		const weekStart = parseDate(getCurrentWeekStart());
		const weekEnd = new Date(weekStart);
		weekEnd.setDate(weekEnd.getDate() + 6);
		return toDateString(weekEnd);
	}, [getCurrentWeekStart]);

	const handlePreviousWeek = () => {
		const currentStart = parseDate(getCurrentWeekStart());
		currentStart.setDate(currentStart.getDate() - 7);
		setSelectedDate(toDateString(currentStart));
	};

	const handleNextWeek = () => {
		const currentStart = parseDate(getCurrentWeekStart());
		currentStart.setDate(currentStart.getDate() + 7);
		setSelectedDate(toDateString(currentStart));
	};

	const handleToday = () => {
		setSelectedDate(new Date().toLocaleDateString("en-CA"));
	};

	useEffect(() => {
		const fetchData = async () => {
			try {
				setIsLoading(true);
				setError(null);
				const startDate = getCurrentWeekStart();
				const endDate = getCurrentWeekEnd();

				const queryParams = new URLSearchParams({
					start_date: startDate,
					end_date: endDate,
				});

				if (clientId) {
					queryParams.set("client", clientId);
				}

				const queryString = queryParams.toString();
				const dashboardResponse = await api.get(
					`/programs/dashboard/?${queryString}`
				);

				setPrograms(dashboardResponse.data.programs);
				setStatusData(dashboardResponse.data.status_data);
			} catch (error) {
				console.error("Error fetching program data:", error);
				setError("Error fetching data. Please try again later.");
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, [clientId, selectedDate, getCurrentWeekEnd, getCurrentWeekStart]);

	const isTherapist = authService.isTherapist();

	const renderUserName = (program) => {
		if (isTherapist) {
			return (
				<small className="text-muted">
					<div>Client: {program.client_name}</div>
					<div>Therapist: {program.therapist_name}</div>
				</small>
			);
		}

		return (
			<small className="text-muted">{program.therapist_name}</small>
		);
	};

	const normaliseStatus = (status) => {
		if (!status) return "pending";

		if (status === "complete") return "completed";
		if (status === "done") return "completed";
		if (status === "partial") return "in-progress";
		if (status === "started") return "in-progress";
		if (status === "not-started") return "pending";

		return status;
	};

	const getStatusValueForDate = (program, date) => {
		const dateString = toDateString(date);
		const programStatusData = statusData[program.id];

		if (!programStatusData) return null;

		if (programStatusData[dateString]) {
			return programStatusData[dateString];
		}

		if (programStatusData.statuses?.[dateString]) {
			return programStatusData.statuses[dateString];
		}

		if (Array.isArray(programStatusData)) {
			return programStatusData.find((item) => {
				const itemDate =
					item.date ||
					item.date_completed ||
					item.completed_date ||
					item.program_date;

				return itemDate === dateString || itemDate?.split("T")[0] === dateString;
			});
		}

		const matchingKey = Object.keys(programStatusData).find((key) => {
			return key.split("T")[0] === dateString;
		});

		if (matchingKey) {
			return programStatusData[matchingKey];
		}

		return null;
	};

	const getProgramStatusData = (program, date) => {
		const value = getStatusValueForDate(program, date);

		if (!value) {
			return {
				status: "pending",
				completedCount: null,
				totalCount: program.exercises?.length ?? program.exercise_count ?? null,
			};
		}

		if (typeof value === "string") {
			return {
				status: normaliseStatus(value),
				completedCount: null,
				totalCount: program.exercises?.length ?? program.exercise_count ?? null,
			};
		}

		const completedCount =
			value.completed_count ??
			value.completed_exercises ??
			value.completedCount ??
			value.completed ??
			null;

		const totalCount =
			value.total_count ??
			value.total_exercises ??
			value.totalCount ??
			value.total ??
			program.exercises?.length ??
			program.exercise_count ??
			null;

		let status = normaliseStatus(value.status);

		if (!value.status && completedCount !== null && totalCount !== null) {
			if (completedCount >= totalCount && totalCount > 0) {
				status = "completed";
			} else if (completedCount > 0) {
				status = "in-progress";
			} else {
				status = "pending";
			}
		}

		return {
			status,
			completedCount,
			totalCount,
		};
	};

	const getProgramStatus = (program, date) => {
		return getProgramStatusData(program, date).status;
	};

	const renderStatusIcon = (status) => {
		if (status === "completed") {
			return (
				<FontAwesomeIcon
					icon={faCircleCheck}
					className="text-success"
					size="2x"
					title="Completed"
				/>
			);
		}

		if (status === "in-progress") {
			return (
				<FontAwesomeIcon
					icon={faCircleHalfStroke}
					className="text-dark"
					size="2x"
					title="Partially completed"
				/>
			);
		}

		if (status === "missed") {
			return <Ban className="text-secondary" size={30} title="Missed" />;
		}

		return null;
	};

	const isTodayOrFuture = (date) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		const targetDate = new Date(date);
		targetDate.setHours(0, 0, 0, 0);

		return targetDate >= today;
	};

	const renderProgramIndicator = (program, date) => {
		const statusIcon = renderStatusIcon(getProgramStatus(program, date));
		const showArrow = !isTherapist && isTodayOrFuture(date);

		if (!statusIcon && !showArrow) {
			return null;
		}

		return (
			<div className="program-card-indicator d-flex align-items-center gap-2">
				{statusIcon}
				{showArrow && (
					<span
						className="program-card-arrow text-primary"
						title="Open program"
						aria-label="Open program"
					>
						<FontAwesomeIcon icon={faArrowRight} />
					</span>
				)}
			</div>
		);
	};

	const getDaysOfWeek = () => {
		return [
			"Monday",
			"Tuesday",
			"Wednesday",
			"Thursday",
			"Friday",
			"Saturday",
			"Sunday",
		];
	};

	const getDateForDayInSelectedWeek = (dayName) => {
		const days = {
			Monday: 0,
			Tuesday: 1,
			Wednesday: 2,
			Thursday: 3,
			Friday: 4,
			Saturday: 5,
			Sunday: 6,
		};
		const date = parseDate(getCurrentWeekStart());
		date.setDate(date.getDate() + days[dayName]);
		return date;
	};

	const isProgramVisibleForDate = (program, date) => {
		const programStartDate = parseDate(program.start_date);
		const dayDate = new Date(date);

		if (program.end_date) {
			const programEndDate = parseDate(program.end_date);
			return programStartDate <= dayDate && dayDate <= programEndDate;
		}

		return programStartDate <= dayDate;
	};

	const isCurrentDay = (date) => {
		const today = new Date();
		return today.toDateString() === date.toDateString();
	};

	const handleProgramClick = (programId, date) => {
		const desiredDate = toDateString(date);
		navigate(`/program/${programId}?date=${desiredDate}`);
	};

	const handleDateChange = (e) => {
		setSelectedDate(e.target.value);
	};

	const getProgramsForDay = (day) => {
		const dayDate = getDateForDayInSelectedWeek(day);
		const dayCode = day.toUpperCase().slice(0, 3);

		return programs.filter(
			(program) =>
				program.days_of_week?.includes(dayCode) &&
				isProgramVisibleForDate(program, dayDate)
		);
	};

	const getDaysWithPrograms = () => {
		return getDaysOfWeek().filter((day) => {
			const dayPrograms = getProgramsForDay(day);
			return dayPrograms.length > 0;
		});
	};

	if (isLoading) return <div className="text-center p-4">Loading...</div>;
	if (error) return <div className="text-center text-danger p-4">{error}</div>;

	return (
		<Container fluid className="px-2 py-4">
			{showOptIn && user && <NotificationOptIn userId={user.id} />}
			{!isTherapist && <ClientMilestones />}

			<Card>
				<Card.Header>
					<div className="d-flex flex-column gap-2">
						<div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
							<h2 className="mb-0">Programs</h2>

							<div className="d-flex align-items-center gap-2 flex-wrap">
								<Button
									variant="outline-secondary"
									size="sm"
									onClick={handlePreviousWeek}
									aria-label="Previous week"
									title="Previous week"
									className="px-3"
								>
									{"<<"}
								</Button>

								<Button
									variant="outline-secondary"
									size="sm"
									onClick={handleToday}
									className="px-3"
								>
									Today
								</Button>

								<Button
									variant="outline-secondary"
									size="sm"
									onClick={handleNextWeek}
									aria-label="Next week"
									title="Next week"
									className="px-3"
								>
									{">>"}
								</Button>

								<Button
									variant="outline-secondary"
									size="sm"
									onClick={() => setShowFilter(!showFilter)}
									className="d-flex align-items-center gap-1 px-3"
									aria-label="Filter programs"
									title="Filter"
								>
									<Filter size={16} />
									<span className="d-none d-sm-inline">Filter</span>
								</Button>
							</div>
						</div>

						<small className="text-muted">
							Showing week:{" "}
							{formatDateRange(getCurrentWeekStart(), getCurrentWeekEnd())}
						</small>

						<Collapse in={showFilter}>
							<div>
								<h6 className="mt-3">Jump to date</h6>
								<Form.Control
									type="date"
									className="mt-2"
									value={selectedDate}
									onChange={handleDateChange}
									max={maxDate}
									min="2020-01-01"
								/>
							</div>
						</Collapse>
					</div>
				</Card.Header>

				<Card.Body>
					{!isTherapist && getDaysWithPrograms().length === 0 ? (
						<div className="text-center p-5">
							<h4
								className="mb-3"
								style={{ color: "#e22e78", fontWeight: "600" }}
							>
								You have no active programs.
							</h4>
							<p className="lead mb-0" style={{ color: "#666" }}>
								Connect with your clinician to get a personalised program and
								unlock your potential!
								<span role="img" aria-label="sparkles" className="ms-2">
									✨
								</span>
							</p>
						</div>
					) : (
						getDaysWithPrograms().map((day) => {
							const dayDate = getDateForDayInSelectedWeek(day);
							const dayPrograms = getProgramsForDay(day);
							const isToday = isCurrentDay(dayDate);

							return (
								<div key={day} className="mb-4">
									<div
										className="program-day-heading"
									>
										<div>
											<h3 className="mb-0">
												{dayDate.toLocaleDateString("en-AU", {
													weekday: "short",
													day: "numeric",
													month: "short",
													year: "numeric",
												})}
											</h3>
										</div>
									</div>

									<Row className="g-3">
										{dayPrograms.map((program) => (
											<Col xs={12} key={program.id}>
												<Card
													onClick={() => handleProgramClick(program.id, dayDate)}
													className={`cursor-pointer program-day-card ${
														isToday && !isTherapist ? "program-day-card-today" : ""
													}`}
												>
													<Card.Body>
														<div className="d-flex justify-content-between align-items-center gap-3">
															<div>
																<h4 className="card-title mb-2">
																	{program.name}
																</h4>
																{renderUserName(program)}
															</div>

															<div className="d-flex flex-shrink-0 align-items-center justify-content-center">
																		{renderProgramIndicator(program, dayDate)}
																	</div>
														</div>
													</Card.Body>
												</Card>
											</Col>
										))}
									</Row>
								</div>
							);
						})
					)}
				</Card.Body>
			</Card>
		</Container>
	);
};

ProgramListComponent.propTypes = {
	clientId: PropTypes.number,
};

export default ProgramListComponent;